"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import Banner from "@/components/library/Banner";
import { BannerItem } from "@/types/common/banner";
import {
  acknowledgeBanner,
  getAcknowledgedBannerIds,
  sortBannersByPriority,
} from "@/utils/banners";
import { apiFetch } from "@/lib/api";

type BannerProps = {
  bannerItems: BannerItem[];
  setBannerItems: (items: BannerItem[]) => void;
};

export const BannerContext = createContext<BannerProps>({
  bannerItems: [],
  setBannerItems: () => {},
});

export const useBanner = () => useContext(BannerContext);

const usePublicBannerFunction = (): BannerProps => {
  const [banners, setBanners] = useState<BannerItem[]>([]);

  const filterBanners = (bannerItems: BannerItem[]) => {
    const alreadyAckedIds = getAcknowledgedBannerIds();

    //remove any banners that have already been seen
    const filteredBanners = bannerItems.filter((banner) => !alreadyAckedIds.includes(banner.id));

    setBanners(sortBannersByPriority(filteredBanners));
  };

  const fetchBanners = async () => {
    const bannerItems = await apiFetch<BannerItem[]>("/api/public/banner");

    if (!bannerItems) {
      setBanners([]);
      return;
    }

    filterBanners(bannerItems);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  return {
    bannerItems: banners,
    setBannerItems: setBanners,
  };
};

export const PublicBannerContextProvider = ({ children }: { children: React.ReactNode }) => {
  const banner = usePublicBannerFunction();
  const { bannerItems, setBannerItems } = banner;

  return (
    <BannerContext.Provider value={banner}>
      {bannerItems.length > 0 ? (
        <Banner
          key={bannerItems[0].id}
          item={bannerItems[0]}
          isPublic={true}
          handleRemoveBanner={async (id: string) => {
            acknowledgeBanner(id);
            setBannerItems(bannerItems.slice(1));
          }}
        />
      ) : null}
      {children}
    </BannerContext.Provider>
  );
};
